import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Building } from '../../interfaces/api/building';
import { fetchBuildings, fetchBuildingsSearchFilter } from '../services/buildings.service';
import { BuildingSearchFilterParam } from '../../api/buildings';

interface BuildingState {
	buildings: {
		[id: number]: Building;
	};
	selectedBuilding: Building | undefined;
	isLoading: boolean;
}

const initialState = {
	buildings: [],
	selectedBuilding: undefined,
	isLoading: false,
} as BuildingState;

export const fetchBuildingsThunk = createAsyncThunk<Building[], void>(
	'building/fetchBuildings',
	async (_undefined, { rejectWithValue }) => {
		try {
			return await fetchBuildings();
		} catch (error) {
			return rejectWithValue(error);
		}
	}
);

export const fetchBuildingsSearchFilterThunk = createAsyncThunk<
	Building[],
	{ params: BuildingSearchFilterParam }
>('building/fetchBuildingsSearchFilter', async ({ params }, { rejectWithValue }) => {
	try {
		return await fetchBuildingsSearchFilter(params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

const buildingSlice = createSlice({
	name: 'building',
	initialState,
	reducers: {
		selectBuilding: (state, action: PayloadAction<number>) => {
			state.selectedBuilding = state.buildings[action.payload];
		},
		unselectBuilding: (state) => {
			state.selectedBuilding = undefined;
		},
		resetBuilding: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchBuildingsThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Building } = {};
			state.buildings = action.payload.reduce((obj, building) => {
				obj[building.id] = building;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchBuildingsThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchBuildingsThunk.rejected, (state, action) => {
			state.buildings = [];
			state.isLoading = false;
		});

		builder.addCase(fetchBuildingsSearchFilterThunk.fulfilled, (state, action) => {
			const ret: { [id: number]: Building } = {};
			state.buildings = action.payload.reduce((obj, building) => {
				obj[building.id] = building;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchBuildingsSearchFilterThunk.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchBuildingsSearchFilterThunk.rejected, (state, action) => {
			state.buildings = [];
			state.isLoading = false;
		});
	},
});

export const { selectBuilding, unselectBuilding, resetBuilding } = buildingSlice.actions;

export default buildingSlice;
